import type { ReactNode } from "react";

/** Ícones de traço das categorias do laboratório, no mesmo desenho da lucide (24×24, cor do texto). */
type Props = { className?: string };

export function Svg({ className, children }: Props & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const DENTE =
  "M8 3C5.8 3 4.5 4.7 4.5 7c0 1.9.8 3.3 1.3 5 .6 2.1.7 4.8 1.4 6.6.3.9.8 1.4 1.4 1.4.9 0 1.3-.9 1.5-2.1l.4-2.6c.2-1.1.7-1.7 1.5-1.7s1.3.6 1.5 1.7l.4 2.6c.2 1.2.6 2.1 1.5 2.1.6 0 1.1-.5 1.4-1.4.7-1.8.8-4.5 1.4-6.6.5-1.7 1.3-3.1 1.3-5 0-2.3-1.3-4-3.5-4-1.6 0-2.4.9-4 .9S9.6 3 8 3z";

/** O dente acima, reduzido e centrado em (x, y); o traço compensa a escala pra ter o mesmo peso. */
export function Dente({ x, y, escala }: { x: number; y: number; escala: number }) {
  return <path d={DENTE} strokeWidth={1.75 / escala} transform={`translate(${x} ${y}) scale(${escala}) translate(-12 -11.5)`} />;
}

export function IconeProteseSobreDente({ className }: Props) {
  return (
    <Svg className={className}>
      <path d="M9 2.5c-1.2 0-2 .8-2 2.1 0 1.1.5 2 .9 2.9h6.2c.4-.9.9-1.8.9-2.9 0-1.3-.8-2.1-2-2.1-.8 0-1.3.4-2 .4s-1.2-.4-2-.4z" />
      <path d="M12 9.5v3M10.6 11.4 12 12.8l1.4-1.4" />
      <path d="M3.4 19c-.6-1.6-.9-3.2-.5-4.6.4-1.3 1.6-1.6 2.6-1.1.8-.4 1.9-.3 2.4.6.6 1.2.4 3.2-.3 5.1" />
      <path d="M20.6 19c.6-1.6.9-3.2.5-4.6-.4-1.3-1.6-1.6-2.6-1.1-.8-.4-1.9-.3-2.4.6-.6 1.2-.4 3.2.3 5.1" />
      <path d="M9.6 14.5h4.8l.5 2.3c-1 .8-1.9 1.1-2.9 1.1s-1.9-.3-2.9-1.1z" />
      <path d="M1.5 20h21" />
    </Svg>
  );
}

export function IconeProteseSobreImplante({ className }: Props) {
  return (
    <Svg className={className}>
      <path d="M8.5 2c-1.6 0-2.5 1.1-2.5 2.8 0 1.5.6 2.6 1.1 3.7h9.8c.5-1.1 1.1-2.2 1.1-3.7C18 3.1 17.1 2 15.5 2c-1.2 0-1.9.6-3.5.6S9.7 2 8.5 2z" />
      <path d="M10.5 8.5v2h3v-2M8.5 10.5h7V12h-7z" />
      <path d="M9.6 12l.6 8c.3 1.3 1 2 1.8 2s1.5-.7 1.8-2l.6-8" />
      <path d="M9.8 14.8 14.2 14M10 17.4l4-.8M10.3 20l3.4-.7" />
    </Svg>
  );
}

export function IconeProtesesProvisorias({ className }: Props) {
  return (
    <Svg className={className}>
      <Dente x={8.4} y={8.7} escala={0.7} />
      <circle cx="17" cy="17" r="5" />
      <path d="M17 14.6V17h2" />
    </Svg>
  );
}

export function IconeProtesesTotais({ className }: Props) {
  return (
    <Svg className={className}>
      <path d="M4 21c-1-1-1.5-2.5-1.5-4.5C2.5 9 6.5 3 12 3s9.5 6 9.5 13.5c0 2-.5 3.5-1.5 4.5" />
      <path d="M7.5 21c-.5-1-.7-2.4-.7-4C6.8 11.5 9 8 12 8s5.2 3.5 5.2 9c0 1.6-.2 3-.7 4" />
      <path d="M4 21h3.5M16.5 21H20" />
      <path d="M2.8 13.5l4.1.6M5 7.8l3.1 2.6M9 4.1l1.3 4.3M15 4.1l-1.3 4.3M19 7.8l-3.1 2.6M21.2 13.5l-4.1.6" />
    </Svg>
  );
}

export function IconePlanejamentoDigital({ className }: Props) {
  return (
    <Svg className={className}>
      <rect x="2.5" y="3" width="19" height="13.5" rx="1.5" />
      <path d="M2.5 13.5h19M10 16.5 9.5 20h5l-.5-3.5M8 20h8" />
      <Dente x={12} y={8.2} escala={0.42} />
    </Svg>
  );
}

export function IconeModelos({ className }: Props) {
  const dentes = [5, 8.5, 12, 15.5];
  return (
    <Svg className={className}>
      <path d="M5 3h14l2 2v3H3V5z" />
      <path d={dentes.map((x) => `M${x} 8v1.6a1.75 1.75 0 0 0 3.5 0V8`).join("")} />
      <path d={dentes.map((x) => `M${x} 15v-1.6a1.75 1.75 0 0 1 3.5 0V15`).join("")} />
      <path d="M3 15h18v3l-2 3H5l-2-3zM3 18h18" />
    </Svg>
  );
}

export function IconePlacas({ className }: Props) {
  return (
    <Svg className={className}>
      <path d="M2.5 7c0 5 4 10.5 9.5 10.5S21.5 12 21.5 7" />
      <path d="M6.5 7.5c.5 3.5 2.8 6 5.5 6s5-2.5 5.5-6" />
      <path d="M2.5 7c1-.8 3-.8 4 .5M21.5 7c-1-.8-3-.8-4 .5" />
      <path d="M5 12.3l2.6-1.5M8.3 15.6l1.3-2.6M12 17.5v-4M15.7 15.6l-1.3-2.6M19 12.3l-2.6-1.5" />
    </Svg>
  );
}

export function IconeAparelhosOrtodonticos({ className }: Props) {
  return (
    <Svg className={className}>
      {[4.4, 12, 19.6].map((x) => (
        <Dente key={x} x={x} y={13} escala={0.5} />
      ))}
      {[4.4, 12, 19.6].map((x) => (
        <rect key={`b${x}`} x={x - 1} y={10.4} width="2" height="2" rx=".4" />
      ))}
      <path d="M1 11.4h22" />
    </Svg>
  );
}
